document.documentElement.classList.remove('no-js');

document.addEventListener('DOMContentLoaded', () => {
  const menu = document.querySelector('.menu-drawer');
  const header = document.querySelector('[data-site-header]');

  if (header) {
    let headerTicking = false;
    const updateHeaderSurface = () => {
      header.classList.toggle('site-header--scrolled', window.scrollY > 24);
      headerTicking = false;
    };
    const requestHeaderUpdate = () => {
      if (!headerTicking) {
        window.requestAnimationFrame(updateHeaderSurface);
        headerTicking = true;
      }
    };

    updateHeaderSurface();
    window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
  }

  if (menu) {
    const menuToggle = menu.querySelector('summary');

    menu.addEventListener('toggle', () => {
      menuToggle?.setAttribute('aria-expanded', String(menu.open));
      menuToggle?.setAttribute('aria-label', menu.open ? 'Close menu' : 'Open menu');
    });

    document.addEventListener('pointerdown', (event) => {
      if (menu.open && !menu.contains(event.target)) {
        menu.removeAttribute('open');
      }
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    }
  });

  const newsletterSuccess = document.querySelector('[data-newsletter-success]');
  if (newsletterSuccess) {
    window.requestAnimationFrame(() => newsletterSuccess.focus({ preventScroll: true }));
  }

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const section = gallery.closest('[data-product-section]');
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const previousButton = gallery.querySelector('[data-gallery-previous]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const status = gallery.querySelector('[data-gallery-status]');

    if (!track || !slides.length) return;

    const slidePosition = (slide) => slide.offsetLeft - slides[0].offsetLeft;
    const currentIndex = () => slides.reduce((closestIndex, slide, index) => {
      const closestDistance = Math.abs(slidePosition(slides[closestIndex]) - track.scrollLeft);
      const distance = Math.abs(slidePosition(slide) - track.scrollLeft);
      return distance < closestDistance ? index : closestIndex;
    }, 0);

    const updateGalleryState = () => {
      const index = currentIndex();
      if (status) status.textContent = `${index + 1} / ${slides.length}`;
      if (previousButton) previousButton.disabled = index === 0;
      if (nextButton) nextButton.disabled = index === slides.length - 1;
    };

    const goToSlide = (index, behavior = 'smooth') => {
      const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!target) return;
      track.scrollTo({ left: slidePosition(target), behavior });
      window.setTimeout(updateGalleryState, behavior === 'smooth' ? 260 : 0);
    };

    gallery.goToMedia = (mediaId, behavior = 'smooth') => {
      if (!mediaId) return;
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) goToSlide(index, behavior);
    };

    previousButton?.addEventListener('click', () => goToSlide(currentIndex() - 1));
    nextButton?.addEventListener('click', () => goToSlide(currentIndex() + 1));

    let galleryTicking = false;
    track.addEventListener('scroll', () => {
      if (galleryTicking) return;
      galleryTicking = true;
      window.requestAnimationFrame(() => {
        updateGalleryState();
        galleryTicking = false;
      });
    }, { passive: true });

    updateGalleryState();
    if (section?.dataset.initialMediaId) {
      window.requestAnimationFrame(() => gallery.goToMedia(section.dataset.initialMediaId, 'auto'));
    }
  });

  document.querySelectorAll('[data-variant-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const selectedOption = select.selectedOptions[0];
      const price = selectedOption?.dataset.price;
      const section = select.closest('[data-product-section]');
      const priceElement = section?.querySelector('[data-product-price]');
      const gallery = section?.querySelector('[data-product-gallery]');
      const addButton = section?.querySelector('[data-add-to-cart]');

      if (price && priceElement) priceElement.textContent = price;
      if (selectedOption?.dataset.mediaId && gallery?.goToMedia) {
        gallery.goToMedia(selectedOption.dataset.mediaId);
      }
      if (addButton) {
        const available = selectedOption?.dataset.available === 'true';
        addButton.disabled = !available;
        addButton.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', select.value);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    });
  });

  document.querySelectorAll('.product-form').forEach((form) => {
    const addButton = form.querySelector('[data-add-to-cart]');
    const feedback = form.querySelector('[data-cart-feedback]');
    const feedbackMessage = form.querySelector('[data-cart-feedback-message]');
    let successTimer;

    form.addEventListener('submit', async (event) => {
      if (event.submitter && !event.submitter.matches('[data-add-to-cart]')) return;

      event.preventDefault();
      window.clearTimeout(successTimer);
      const originalLabel = addButton.textContent;
      const root = window.Shopify?.routes?.root || '/';
      let addedSuccessfully = false;
      addButton.classList.remove('is-added');
      addButton.disabled = true;
      addButton.textContent = 'ADDING...';
      if (feedback) feedback.hidden = true;

      try {
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(form),
          credentials: 'same-origin'
        });
        const addedItem = await addResponse.json();
        if (!addResponse.ok) throw new Error(addedItem.description || 'This item could not be added.');

        const cartResponse = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        const cart = await cartResponse.json();

        document.querySelectorAll('.cart-link__count').forEach((count) => {
          count.textContent = cart.item_count;
        });
        document.querySelectorAll('.cart-link').forEach((link) => {
          link.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
        });

        addedSuccessfully = true;
        addButton.classList.add('is-added');
        addButton.textContent = 'ADDED TO CART \u2713';
        if (feedback && feedbackMessage) feedbackMessage.textContent = 'Added to cart.';
      } catch (error) {
        if (feedback && feedbackMessage) {
          feedbackMessage.textContent = error.message || 'This item could not be added.';
          feedback.hidden = false;
        }
      } finally {
        const selectedOption = form.querySelector('[data-variant-select]')?.selectedOptions[0];
        const restoreButton = () => {
          addButton.classList.remove('is-added');
          addButton.disabled = selectedOption?.dataset.available === 'false';
          addButton.textContent = originalLabel;
        };

        if (addedSuccessfully) {
          addButton.disabled = false;
          successTimer = window.setTimeout(restoreButton, 1800);
        } else {
          restoreButton();
        }
      }
    });
  });
});
